'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { Typography } from '@musinsa/mds';
import { useDialog } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { deleteCurationService } from '@musinsa/services-curator';
import { useMutation, useQuery } from '@tanstack/react-query';
import { twMerge } from 'tailwind-merge';
import { useCuratorBroadcastPublisher } from '../../common';
import { useCuratorSnackbar } from '../../common/hooks';
import { curatorQueries } from '../../query-factory';

interface Props {
  className?: string;
  title: string;
  curationId: string;
  curationType?: 'GOODS' | 'SNAP2' | null;
  onClose: () => void;
}

export const CurationDeleteButton = ({
  className,
  title,
  curationId,
  curationType,
  onClose,
}: Props) => {
  const snackbar = useCuratorSnackbar();
  const { triggerQuerySync } = useCuratorBroadcastPublisher();
  const dialog = useDialog();
  const { service } = useDeviceInfo();
  const { data: userInfo } = useQuery({ ...curatorQueries.profile.userInfo });

  const { mutate: deleteCuration } = useMutation({
    mutationFn: deleteCurationService,
    onSuccess: () => {
      triggerQuerySync({
        queryKeys: [
          curatorQueries.curations.myList._def,
          curatorQueries.curations.checkCurationCreatable().queryKey,
        ],
      });
      snackbar.success('큐레이션이 삭제되었습니다.');

      dialog.close();
      onClose();
    },
    onError: (error) => {
      snackbar.error(error.message);
    },
  });

  const handleClickOpenDialog = () => {
    if (service === SERVICE.MUSINSA) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'curation_edit_select',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: userInfo?.snsTitle ?? '',
        name: 'delete',
        section_name: 'curator_curationfeed',
        type: curationType === 'SNAP2' ? 'snap' : 'goods',
      });
    }

    // BottomSheet 닫기
    onClose();

    // Dialog 열기
    dialog.open({
      title: `'${title}' 큐레이션을 삭제하시겠습니까?`,
      confirmButtonText: '삭제하기',
      cancelButtonText: '취소',
      onConfirm: () => deleteCuration({ curationId }),
    });

    // NOTE: MDS 시스템 내에서 pointer-events 를 의도치않게 넣어주고있어서, 수동으로 body 스타일 제거해야함
    document.body.style.removeProperty('pointer-events');
  };

  return (
    <Typography
      className={twMerge('py-[12px]! space-y-[11px] cursor-pointer', className)}
      colorScheme="gray-600"
      variant="body13pxReg"
      onClick={handleClickOpenDialog}
    >
      삭제하기
    </Typography>
  );
};
